import React, { useRef, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { FormattedMessage } from 'react-intl'

import useGlobalFunction from 'src/hooks/useGlobalFunction'
import useScript from 'src/hooks/useScript'

import messages from './messages'
import {
  JET_IFRAME_GLOBAL_CALLBACK_FUNCTION,
  JET_IFRAME_INPUT_STYLES,
  JET_IFRAME_YEARS_COUNT_SELECTOR,
  CONFIRM_BUTTON_SUBMIT_VARIANT,
  CHANGE_CREDIT_CARD_BUTTON_SUBMIT_VARIANT,
} from './constants'
import { JetInputWrapper, JetInputDatesWrapper } from './styles'
import PrimaryButton from 'src/comps/Atom/Buttons/PrimaryButton'

const JetIframeForm = ({
  providerId,
  purchaseId,
  onSuccess,
  resetCallback = () => {},
  disabled = false,
  submitCopy = CONFIRM_BUTTON_SUBMIT_VARIANT,
  scriptUrl,
  customSubmit,
  onBeforeSubmit,
}) => {
  const formRef = useRef()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = async event => {
    event.preventDefault()
    if (typeof onBeforeSubmit === 'function') {
      setIsSubmitting(true)
      if (!(await onBeforeSubmit())) {
        setIsSubmitting(false)
        return
      }
    }
    formRef.current.dispatchEvent(new Event('submit'))
  }

  async function onFormSubmit() {
    const requestValues = Array.from(formRef.current).reduce((formValues, input = {}) => ({
      ...formValues,
      [input.name]: input.value,
    }))

    onSuccess({ ...requestValues, purchaseId })
  }

  function getNextYears() {
    return Array.from({ length: JET_IFRAME_YEARS_COUNT_SELECTOR }, (_, i) =>
      (new Date().getFullYear() + i).toString().substr(-2)
    )
  }

  useGlobalFunction(JET_IFRAME_GLOBAL_CALLBACK_FUNCTION, onFormSubmit)
  const scriptStatus = useScript(scriptUrl)

  useEffect(() => {
    const creditCardNumberInputs = document.querySelectorAll('#paycomet-pan > iframe').length
    const cvcInputs = document.querySelectorAll('#paycomet-cvc2 > iframe').length

    if (scriptStatus === 'ready' && creditCardNumberInputs === 0 && cvcInputs === 0) {
      resetCallback()
    }
  }, [scriptStatus]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <form
      id="paycometPaymentForm"
      action={`javascript:${JET_IFRAME_GLOBAL_CALLBACK_FUNCTION}();`}
      method="POST"
      ref={formRef}
    >
      <input name="jetID" type="hidden" data-paycomet="jetID" value={providerId} />
      <input type="hidden" data-paycomet="no-mask" value="" />

      <JetInputWrapper>
        <label htmlFor="pan">
          <FormattedMessage {...messages.cardNumber} />
        </label>
        <div id="paycomet-pan" data-paycomet="paNumber" />
        <input
          paycomet-style={JET_IFRAME_INPUT_STYLES} // eslint-disable-line
          name="pan"
          type="text"
          id="old-pan-input"
          paycomet-name="pan" // eslint-disable-line
        />
      </JetInputWrapper>
      <JetInputWrapper>
        <label htmlFor="cardHolderName">
          <FormattedMessage {...messages.cardHolder} />
        </label>
        <input name="cardHolderName" type="text" data-paycomet="cardHolderName" />
      </JetInputWrapper>

      <JetInputWrapper>
        <label htmlFor="dateMonth">
          <FormattedMessage {...messages.expiryDate} />
        </label>
        <JetInputDatesWrapper>
          <select data-paycomet="dateMonth">
            <option>MM</option>
            <option value="01">01</option>
            <option value="02">02</option>
            <option value="03">03</option>
            <option value="04">04</option>
            <option value="05">05</option>
            <option value="06">06</option>
            <option value="07">07</option>
            <option value="08">08</option>
            <option value="09">09</option>
            <option value="10">10</option>
            <option value="11">11</option>
            <option value="12">12</option>
          </select>
          <span>/</span>
          <select data-paycomet="dateYear">
            <option>YY</option>
            {getNextYears().map(year => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </JetInputDatesWrapper>
      </JetInputWrapper>

      <JetInputWrapper>
        <label htmlFor="cvc">
          <FormattedMessage {...messages.cvcCode} />
        </label>
        <div id="paycomet-cvc2" data-paycomet="cvc2" />
        <input
          paycomet-style={JET_IFRAME_INPUT_STYLES} // eslint-disable-line
          name="cvc"
          type="text"
          id="old-CVC2-input"
          paycomet-name="cvc2" // eslint-disable-line
        />
      </JetInputWrapper>

      <div id="paymentErrorMsg" />
      {customSubmit ? (
        <PrimaryButton onClick={handleSubmit} disabled={disabled || isSubmitting} fullWidth>
          <FormattedMessage {...messages[submitCopy]} />
        </PrimaryButton>
      ) : (
        <PrimaryButton onClick={handleSubmit} disabled={disabled} fullWidth>
          <FormattedMessage {...messages[submitCopy]} />
        </PrimaryButton>
      )}
    </form>
  )
}

JetIframeForm.propTypes = {
  providerId: PropTypes.string.isRequired,
  purchaseId: PropTypes.string.isRequired,
  onSuccess: PropTypes.func.isRequired,
  resetCallback: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  submitCopy: PropTypes.oneOf([
    CONFIRM_BUTTON_SUBMIT_VARIANT,
    CHANGE_CREDIT_CARD_BUTTON_SUBMIT_VARIANT,
  ]),
  scriptUrl: PropTypes.string.isRequired,
  onBeforeSubmit: PropTypes.func,
  customSubmit: PropTypes.bool,
}

export default JetIframeForm
